import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/layouts/MainLayout';
import { theme } from '../../config/theme';
import { useToast } from '../../components/common/Toast';
import barterService from '../../services/barter.service';

const BarterCreate = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    exchange_with: '',
    description: '',
    price: '',
    condition: '',
    whatsapp_number: '',
    contact_number: '',
    images: []
  });

  const fileInputRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const newImages = Array.from(e.target.files);
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...newImages]
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.exchange_with || !formData.price || !formData.condition) {
      return toast.error('Please fill in all required fields');
    }

    try {
      setLoading(true);
      const res = await barterService.create(formData, formData.images);
      if (res.success) {
        toast.success('Barter product uploaded successfully');
        navigate('/seller/barter');
      }
    } catch (error) {
      console.error('Error uploading barter product:', error);
      toast.error(error.response?.data?.message || 'Failed to upload barter product');
    } finally {
      setLoading(false);
    }
  };

  // Theme matching colors
  const indigo = {
    50:  '#eef2ff',
    100: '#e0e7ff',
    400: '#818cf8',
    500: '#6366f1',
    600: '#4f46e5',
    700: '#4338ca',
  };
  const slateColor = '#475569';
  const slateLight = '#f8fafc';

  return (
    <MainLayout>
      <div style={{ padding: theme.spacing.xl, maxWidth: '600px', margin: '0 auto', fontFamily: theme.typography.fontFamily }}>
        <div style={{ marginBottom: theme.spacing.xl, borderBottom: `1px solid ${theme.colors.border.light}`, paddingBottom: theme.spacing.md }}>
          <h1 style={{ 
            fontSize: theme.typography.fontSize['3xl'], 
            fontWeight: theme.typography.fontWeight.bold,
            color: slateColor,
            margin: 0
          }}>
            Upload Product for Barter
          </h1>
          <p style={{ color: theme.colors.text.secondary, marginTop: theme.spacing.sm }}>
            Fill in the details below to list your item for exchange.
          </p>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.lg, backgroundColor: theme.colors.white, padding: theme.spacing.xl, borderRadius: theme.borderRadius.lg, boxShadow: theme.shadows.md }}>
          
          {/* Image Upload Button */}
          <div>
            <input 
              type="file" 
              ref={fileInputRef} 
              onChange={handleImageChange} 
              style={{ display: 'none' }} 
              multiple 
              accept="image/*"
              aria-label="Choose Photos"
            />
            <button 
              type="button"
              onClick={handleImageClick}
              style={{
                width: '100%',
                padding: theme.spacing.xl,
                backgroundColor: slateLight,
                border: `2px dashed ${theme.colors.gray[300]}`,
                borderRadius: theme.borderRadius.md,
                color: slateColor,
                fontWeight: theme.typography.fontWeight.medium,
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                transition: theme.transitions.base,
              }}
              onMouseOver={(e) => e.currentTarget.style.borderColor = indigo[600]}
              onMouseOut={(e) => e.currentTarget.style.borderColor = theme.colors.gray[300]}
            >
              + Choose Photos
            </button>
            {formData.images.length > 0 && (
              <p style={{ fontSize: theme.typography.fontSize.xs, color: slateColor, marginTop: theme.spacing.xs }}>
                {formData.images.length} photos selected
              </p>
            )}
          </div>

          {/* Title */}
          <div>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="Title"
              required
              aria-label="Title"
              style={inputStyle}
            />
          </div>

          {/* Exchange With */}
          <div>
            <input
              type="text"
              name="exchange_with"
              value={formData.exchange_with}
              onChange={handleChange}
              placeholder="What would you like to exchange with?"
              required
              aria-label="What would you like to exchange with?"
              style={inputStyle}
            />
          </div>

          {/* Description */}
          <div>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Description..."
              required
              aria-label="Description"
              rows="5"
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </div>

          {/* Price */}
          <div>
            <input
              type="number"
              name="price"
              value={formData.price}
              onChange={handleChange}
              placeholder="Enter the current price of your product"
              min="0"
              step="0.01"
              required
              aria-label="Enter the current price of your product"
              style={inputStyle}
            />
          </div>

          {/* Condition Dropdown */}
          <div>
            <select
              name="condition"
              value={formData.condition}
              onChange={handleChange}
              required
              aria-label="Product Condition"
              style={inputStyle}
            >
              <option value="" disabled>New / Used</option>
              <option value="new">New</option>
              <option value="used">Used</option>
            </select>
          </div>

          {/* WhatsApp Contact Number */}
          <div>
            <input
              type="tel"
              name="whatsapp_number"
              value={formData.whatsapp_number}
              onChange={handleChange}
              placeholder="WhatsApp contact number"
              required
              aria-label="WhatsApp contact number"
              style={inputStyle}
            />
          </div>

          {/* Contact Number */}
          <div>
            <input
              type="tel"
              name="contact_number"
              value={formData.contact_number}
              onChange={handleChange}
              placeholder="Contact number"
              required
              aria-label="Contact number"
              style={inputStyle}
            />
          </div>

          {/* Submit Button */}
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: theme.spacing.lg }}>
            <button
              type="submit"
              style={{
                background: `linear-gradient(135deg, ${indigo[500]}, ${indigo[700]})`,
                color: theme.colors.white,
                padding: `${theme.spacing.md} ${theme.spacing['2xl']}`,
                borderRadius: theme.borderRadius.md,
                border: 'none',
                fontWeight: theme.typography.fontWeight.semibold,
                fontSize: theme.typography.fontSize.base,
                cursor: 'pointer',
                transition: theme.transitions.base,
                width: '100%',
                maxWidth: '300px',
                boxShadow: `0 4px 14px ${indigo[100]}`,
              }}
              onMouseOver={(e) => e.currentTarget.style.opacity = '0.9'}
              onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
              disabled={loading}
            >
              {loading ? 'Uploading...' : 'Upload'}
            </button>
          </div>
        </form>
      </div>
    </MainLayout>
  );
};

const inputStyle = {
  width: '100%',
  padding: theme.spacing.md,
  border: `1px solid ${theme.colors.border.main}`,
  borderRadius: theme.borderRadius.md,
  fontSize: theme.typography.fontSize.sm,
  fontFamily: theme.typography.fontFamily,
  color: theme.colors.text.primary,
  outline: 'none',
  boxSizing: 'border-box'
};

export default BarterCreate;
